import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { CopyableId, DetailRow, EnabledToolsChips } from './simulationUtils';

const ExternalLink = ({ href }) =>
  href ? (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      variant={'body2'}
      sx={{ wordBreak: 'break-all' }}
    >
      {href}
    </Link>
  ) : (
    <Typography variant={'body2'}>--</Typography>
  );

const PersonaText = ({ text, fallback }) => (
  <Typography
    variant={'body2'}
    color={text ? 'text.primary' : 'text.secondary'}
    sx={{ whiteSpace: 'pre-wrap' }}
  >
    {text || fallback}
  </Typography>
);

// Read-only view of what was submitted at creation (GET /{id}/setup).
const SimulationSetup = ({ setup, fetching, error, onRefresh, onCopied }) => {
  if (fetching && !setup) {
    return (
      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'center'}
        gap={1}
        py={6}
      >
        <CircularProgress size={20} />
        <Typography variant={'body2'} color={'text.secondary'}>
          Loading setup...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={onRefresh}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (!setup) return null;

  const buyers = setup?.buyerContacts || [];

  return (
    <Box display={'flex'} flexDirection={'column'} gap={2}>
      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        gap={1}
      >
        <Typography variant={'body2'} color={'text.secondary'}>
          What was entered when this simulation was created. Buyers added later,
          persona edits and the runtime email each agent uses are on the Agents
          tab.
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={onRefresh}
          disabled={fetching}
          startIcon={<Refresh fontSize="small" />}
          sx={{ flexShrink: 0 }}
        >
          Refresh
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant={'subtitle2'} fontWeight={'bold'} mb={1}>
          Company
        </Typography>
        <DetailRow label="Website">
          <ExternalLink href={setup?.companyWebsite} />
        </DetailRow>
        <DetailRow label="LinkedIn">
          <ExternalLink href={setup?.companyLinkedInUrl} />
        </DetailRow>
        <DetailRow label="Enabled tools">
          <EnabledToolsChips tools={setup?.enabledTools} />
        </DetailRow>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant={'subtitle2'} fontWeight={'bold'} mb={1}>
          Seller
        </Typography>
        <DetailRow label="Name">
          <Typography variant={'body2'}>
            {setup?.sellerDisplayName || '--'}
          </Typography>
        </DetailRow>
        <DetailRow label="Email">
          <Typography variant={'body2'}>
            {setup?.sellerEmail || '--'}
          </Typography>
        </DetailRow>
        <DetailRow label="User ID">
          <CopyableId value={setup?.sellerUserId} onCopied={onCopied} />
        </DetailRow>
        <DetailRow label="Persona override">
          <PersonaText
            text={setup?.sellerPersonaOverride}
            fallback="None — uses the seller’s default persona"
          />
        </DetailRow>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant={'subtitle2'} fontWeight={'bold'} mb={1}>
          Buyer contacts ({buyers.length})
        </Typography>
        <Box display={'flex'} flexDirection={'column'} gap={1.5}>
          {buyers.map((buyer, index) => (
            <Paper
              key={`${buyer?.name}-${index}`}
              variant="outlined"
              sx={{ p: 1.5 }}
            >
              <Typography variant={'body2'} fontWeight={'bold'}>
                {buyer?.name || '--'}
                {buyer?.title ? ` — ${buyer.title}` : ''}
              </Typography>
              <Typography
                variant={'caption'}
                color={'text.secondary'}
                display={'block'}
                mb={1}
              >
                {buyer?.email ||
                  'Email not provided — one was generated at runtime'}
              </Typography>
              <PersonaText text={buyer?.persona} fallback="No persona" />
            </Paper>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

export default SimulationSetup;
